import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './TestGoogleConnection.css';

const TestGoogleConnection = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  // Check if we're returning from OAuth flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authResult = urlParams.get('auth');
    const page = urlParams.get('page');
    
    if (authResult === 'success' && page === 'test-google-connection') {
      setConnectionStatus('success');
      setError(null);
      // Clean up URL
      window.history.replaceState({}, document.title, '/test-google-connection');
    } else if (authResult === 'error' && page === 'test-google-connection') {
      setConnectionStatus('error');
      setError('OAuth flow failed. Please try again.');
      // Clean up URL
      window.history.replaceState({}, document.title, '/test-google-connection');
    }
  }, []);

  const handleConnectToGoogle = async () => {
    setIsConnecting(true);
    setError(null);
    setConnectionStatus(null);

    try {
      const response = await apiCall('/api/test-google-connection/oauth/initiate', 'GET');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.auth_url) {
          // Open Google OAuth in new window/tab
          window.location.href = data.auth_url;
        } else {
          setError('Failed to get authorization URL');
          setConnectionStatus('error');
        }
      } else {
        setError('Failed to get authorization URL');
        setConnectionStatus('error');
      }
    } catch (err) {
      setError(`Failed to initiate OAuth: ${err.message}`);
      setConnectionStatus('error');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setError(null);
    setTestResult(null);

    try {
      const response = await apiCall('/api/test-google-connection/test', 'GET');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          setTestResult({
            status: 'success',
            message: data.message,
            details: data.details
          });
          // If test is successful, we know we have valid tokens
          setConnectionStatus('success');
        } else {
          setTestResult({
            status: 'error',
            message: data.error || 'Test failed'
          });
        }
      } else {
        setTestResult({
          status: 'error',
          message: 'Test failed'
        });
      }
    } catch (err) {
      setTestResult({
        status: 'error',
        message: `Test failed: ${err.message}`
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="test-google-connection">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Test Google Connection</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Test the Google OAuth2 flow with your personal Google account</li>
              <li>Verify that tokens are properly stored and refreshed</li>
              <li>Ensure Google Drive API access is working correctly</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h2>Step 1: Connect to Google</h2>
        </div>
        
        <div className="connection-status">
          {connectionStatus === 'success' && (
            <div className="status-success">
              ✅ Successfully connected to Google! Tokens have been stored.
            </div>
          )}
          
          {connectionStatus === 'error' && (
            <div className="status-error">
              ❌ Connection failed. Please try again.
            </div>
          )}
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="button-container">
          <button 
            className="connect-button"
            onClick={handleConnectToGoogle}
            disabled={isConnecting}
          >
            {isConnecting ? 'Connecting...' : 'Connect to Google'}
          </button>
        </div>

        <div className="instructions">
          <h3>Instructions:</h3>
          <ol>
            <li>Click "Connect to Google" to start the OAuth2 flow</li>
            <li>You'll be redirected to Google's authorization page</li>
            <li>Log in with your Google account and grant permissions</li>
            <li>You'll be redirected back here with new tokens stored</li>
          </ol>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h2>Step 2: Test Connection</h2>
        </div>
        
        {testResult && (
          <div className={`test-result ${testResult.status}`}>
            <h3>Test Result:</h3>
            <p>{testResult.message}</p>
            {testResult.details && (
              <div className="test-details">
                <h4>Details:</h4>
                <pre>{JSON.stringify(testResult.details, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        <div className="button-container">
          <button 
            className="test-button"
            onClick={handleTestConnection}
            disabled={isTesting}
          >
            {isTesting ? 'Testing...' : 'Test Google Connection'}
          </button>
        </div>

        <div className="instructions">
          <h3>What this test does:</h3>
          <ul>
            <li>Verifies that the stored tokens are valid</li>
            <li>Tests access to Google Drive API</li>
            <li>Automatically refreshes tokens if needed</li>
            <li>Lists available Google Drive folders</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TestGoogleConnection; 